/**
 * views/EmailAnalyzerView.js
 * Propósito: Vista para análisis de emails con textarea multi-línea (requiere autenticación).
 */

import React, { useEffect, useState } from 'react';
import EmailReportCard from '../components/EmailReportCard';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

export default function EmailAnalyzerView() {
  const { user, token } = useAuth();
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Si el usuario no está autenticado, abrir el modal al entrar a la vista
  useEffect(() => {
    if (!user || !token) {
      setShowAuthModal(true);
    }
  }, [user, token]);
  
  const handleAnalyze = async () => {
    setError('');
    setResults([]);
    
    // Verificar autenticación
    if (!user || !token) {
      setShowAuthModal(true);
      return;
    }
    
    // Parsear emails (uno por línea)
    const emails = emailInput
      .split('\n')
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    if (emails.length === 0) {
      setError('Ingresa al menos un email');
      return;
    }
    
    if (emails.length > 50) {
      setError('Máximo 50 emails por análisis');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.analyzeEmails(emails, token);
      
      if (response && response.data) {
        setResults(response.data);
      }
    } catch (err) {
      setError(err.message || 'Error al analizar emails');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <div className="container my-4">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card" style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '15px'
            }}>
              <div className="card-body">
                <h2 className="text-center mb-3" style={{ color: '#fff' }}>
                  📧 Análisis de Emails
                </h2>
                
                {!user && (
                  <div className="alert alert-info text-center mb-3" style={{
                    background: 'rgba(13, 110, 253, 0.2)',
                    border: '1px solid rgba(13, 110, 253, 0.4)',
                    color: '#fff'
                  }}>
                    <strong>🔒 Función protegida</strong>
                    <p className="mb-0 mt-2">
                      Debes iniciar sesión o registrarte para usar el análisis de emails
                    </p>
                  </div>
                )}
                
                <p className="text-center" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Ingresa uno o más emails (uno por línea) para validar sintaxis, DNS, dominios desechables y typosquatting.
                </p>
                
                <div className="mb-3">
                  <textarea
                    className="form-control"
                    rows="6"
                    placeholder="ejemplo@gmail.com&#10;otro@yahoo.com&#10;test@dominio.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    disabled={loading}
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: '#fff',
                      borderRadius: '10px'
                    }}
                  />
                  <small style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Máximo 50 emails por análisis
                  </small>
                </div>
                
                {error && (
                  <div className="alert alert-danger">{error}</div>
                )}
                
                <button 
                  className="btn btn-primary w-100"
                  onClick={handleAnalyze}
                  disabled={loading}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px'
                  }}
                >
                  {loading ? 'Analizando...' : 'Analizar Emails'}
                </button>
              </div>
            </div>
            
            {results.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-3" style={{ color: '#fff' }}>
                  Resultados ({results.length})
                </h4>
                
                {results.map((report, i) => (
                  <EmailReportCard key={i} report={report} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal de autenticación */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      )}
    </>
  );
}
